import {Component, OnInit, Type} from '@angular/core';
import {AbstractApplicationFlowComponent} from '../application-flow/abstract-application-flow.component';
import {Step} from '../application-flow/steps/step';
import {BorrowedInfoStep} from '../application-flow/steps/borrowed-info.steps';
import {LoanApplicationFlowNavigator} from '../application-flow/loan-application-flow.navigator';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {BorrowedInfoInteractor} from '../interactors/borrowed-info.interactor';
import {Logger} from '../utils/Logger';
import {BorrowedInfo, BorrowedStatus} from '../model/borrowed-info.model';
import {LoanApplicationInteractor} from '../interactors/loan-application.interactor';
import {Obligations} from '../model/eligibility-check/eligibility-check-request.model';
import {ObligationStatus} from '../model/obligation-status.model';
import {ActivatedRoute} from '@angular/router';
import {executeWithLoader, LoaderStatus} from '../utils/loader.util';

@Component({
    templateUrl: 'borrowed-info.component.html',
    styleUrls: ['./borrowed-info.component.css', '../loan-application-flow-component/application-flow.css']
})
export class BorrowedInfoComponent extends AbstractApplicationFlowComponent implements OnInit {
    public debtFormGroup: FormGroup;
    public borrowedStatus: BorrowedStatus;
    public borrowedInfo: BorrowedInfo;
    public readonly BorrowedOptions = [
        {displayName: 'Yes, I have loans', value: BorrowedStatus.BORROWED},
        {displayName: 'No, I dont have loans', value: BorrowedStatus.NOT_BORROWED},
    ];
    public readonly NoOfLoansOptions = [
        {displayName: '1', value: 1},
        {displayName: '2', value: 2},
        {displayName: '3', value: 3},
        {displayName: '3+', value: 4},
    ];
    loaderStatus = new LoaderStatus();

    constructor(loanApplicationFlowNavigator: LoanApplicationFlowNavigator, formBuilder: FormBuilder,
                private borrowedInfoInteractor: BorrowedInfoInteractor,
                private loanApplicationInteractor: LoanApplicationInteractor,
                private activatedRoute: ActivatedRoute) {
        super(loanApplicationFlowNavigator);
        this.borrowedStatus = BorrowedStatus.BORROWED;
        this.debtFormGroup = formBuilder.group({
            borrowedStatus: formBuilder.control(BorrowedStatus.BORROWED),
            noOfLoans: formBuilder.control('1'),
            sumOfEmis: formBuilder.control(null, [Validators.min(1), Validators.required])
        });

        this.activatedRoute.data.subscribe(data => {
            const obligations: Obligations = data.obligations;
            if (obligations) {
                this.debtFormGroup.patchValue({
                    borrowedStatus: obligations.getStatus() === ObligationStatus.HAVE_LOAN ? (BorrowedStatus.BORROWED) : (BorrowedStatus.NOT_BORROWED),
                    sumOfEmis: obligations.emi
                });
            }
        });
    }

    async ngOnInit(): Promise<void> {
        await this.preFill(this.debtFormGroup);
    }

    step(): Type<Step> {
        return BorrowedInfoStep;
    }

    async preFill(formGroup: FormGroup): Promise<void> {
        const loanApplication = await this.loanApplicationInteractor.get();
        const borrowedInfo = await this.borrowedInfoInteractor.getByLoanApplicationId(loanApplication.id);

        if (!!borrowedInfo) {
            this.borrowedStatus = borrowedInfo.borrowedStatus;
            this.borrowedInfo = borrowedInfo;

            if (borrowedInfo.borrowedStatus === BorrowedStatus.BORROWED) {
                formGroup.patchValue({
                    sumOfEmis: borrowedInfo.sumOfEmis,
                    noOfLoans: borrowedInfo.noOfLoans ? borrowedInfo.noOfLoans.toString() : undefined
                });
            }
        }
    }

    async onSubmit(formGroup: FormGroup) {
        if (this.borrowedStatus === BorrowedStatus.BORROWED) {
            if (formGroup.invalid) {
                Logger.log(`Form is not valid. Not going ahead with the submission.`);
                return;
            }
        }

        await executeWithLoader(this.loaderStatus, async () => {
            const loanApplicationId = (await this.loanApplicationInteractor.get()).id;
            const borrowedInfo = new BorrowedInfo(this.borrowedStatus, loanApplicationId);

            if (this.borrowedStatus === BorrowedStatus.BORROWED) {
                const noOfLoans = formGroup.controls.noOfLoans.value;
                const sumOfEmis = formGroup.controls.sumOfEmis.value;
                borrowedInfo.noOfLoans = noOfLoans;
                borrowedInfo.sumOfEmis = sumOfEmis;
            }

            borrowedInfo.id = this.borrowedInfo ? this.borrowedInfo.id : null;
            borrowedInfo.version = this.borrowedInfo ? this.borrowedInfo.version : null;

            this.borrowedInfo = await this.borrowedInfoInteractor.createOrUpdateBorrowedInfo(borrowedInfo);
            await this.nextPage();
        });
    }

    showFieldsForDebtInformation(): boolean {
        return this.borrowedStatus === BorrowedStatus.BORROWED;
    }

    protected errorMessages(): object {
        // TODO make messages better
        return {
            sumOfEmis: [{
                type: 'required',
                message: 'Please enter the total amount of emis you pay'
            }, {
                type: 'min',
                message: 'Please enter the minimum amount for emis you pay'
            }]
        };
    }
}
